// Canonical per-session semester pricing.
//
// One place computes "what does a parent pay for this semester session?" so
// the public card, the detail dialog, the registration selector, checkout,
// confirmation emails and admin previews can never disagree.
//
// Rules:
//   • A price stored on the session row (classes.sessions `price`) is the
//     amount checkout charges — it always wins.
//   • Otherwise the amount is derived from the class's structured billing unit
//     and the academic calendar (never hardcoded week counts).

import type { ClassItem, ClassSession } from "@/data/classes";
import {
  getSemesterMonthCount,
  getSemesterWindowForWeekday,
  semesterIdFromLabel,
  type WeekdayName,
} from "@/lib/academicCalendar";
import { parseWeekdays } from "@/lib/sessionSchedule";
import { coerceBillingUnit, computeSemesterTotal } from "@/lib/classPricing";

/** Per-child total for a semester session, or null when it isn't a semester. */
export function semesterTotalForSession(
  classItem: Pick<
    ClassItem,
    "billingUnit" | "unitPrice" | "schedule" | "managementType" | "hasInstructor" | "sessionLengthWeeks"
  >,
  session: Pick<ClassSession, "label" | "dates" | "price">,
): number | null {
  const semester = semesterIdFromLabel(session.label);
  if (!semester) return null;
  const weekdays = [
    ...parseWeekdays(session.dates),
    ...parseWeekdays(classItem.schedule),
  ] as WeekdayName[];
  const primaryWeekday = weekdays[0];
  const win = primaryWeekday
    ? getSemesterWindowForWeekday(semester, primaryWeekday)
    : null;
  const instructionalWeeks =
    win?.weeks ??
    Math.max(
      0,
      ...weekdays.map((w) => getSemesterWindowForWeekday(semester, w)?.weeks ?? 0),
    );
  return computeSemesterTotal({
    billingUnit: coerceBillingUnit(classItem.billingUnit),
    pricePerChild: classItem.unitPrice,
    sessionLengthWeeks: classItem.sessionLengthWeeks ?? 1,
    instructionalWeeks,
    semesterMonths: getSemesterMonthCount(semester),
    externalPayment: classItem.managementType === "external_provider",
    hasInstructor: classItem.hasInstructor,
    semesterPriceOverride: session.price ?? null,
  });
}
